import { formatLocal, formatRelative, shortSession } from "./formatting";

export type ContentFilter = "text" | "tool_use" | "tool_result" | "thinking" | "system";

// Accepts either a prime row (has channel + role) or a briefing row (neither).
// Author is a superset; briefing rows only ever produce "system" or "agent".
export interface MessageBlockItem {
  id: string;
  called_at: string;
  session_id: string | null;
  channel?: string;
  role?: string;
  message_type: string;
  author: "human" | "agent" | "tool" | "system";
  payload: unknown;
}

interface Props {
  item: MessageBlockItem;
  contentFilters?: Set<ContentFilter>;
}

interface ContentBlock {
  type: string;
  text?: string;
  name?: string;
  input?: unknown;
  tool_use_id?: string;
  content?: unknown;
}

interface SDKPayload {
  type?: string;
  message?: {
    content?: ContentBlock[];
  };
  result?: string;
  total_cost_usd?: number;
  num_turns?: number;
  duration_ms?: number;
  stop_reason?: string;
}

function extractContent(payload: unknown): ContentBlock[] | null {
  const p = payload as SDKPayload | null;
  const content = p?.message?.content;
  return Array.isArray(content) ? content : null;
}

function TextBlock({ text }: { text: string }) {
  return <div className="message-text">{text}</div>;
}

function ToolUseCard({ block }: { block: ContentBlock }) {
  return (
    <details className="tool-use-card">
      <summary>
        → <span className="tool-name">{block.name ?? "tool"}</span>
      </summary>
      <pre>{JSON.stringify(block.input ?? {}, null, 2)}</pre>
    </details>
  );
}

function ToolResultCard({ block }: { block: ContentBlock }) {
  const content = block.content;
  let rendered: string;
  if (typeof content === "string") {
    rendered = content;
  } else if (Array.isArray(content)) {
    rendered = content
      .map((c) => (typeof c === "object" && c && "text" in c ? String((c as { text: unknown }).text) : JSON.stringify(c)))
      .join("\n");
  } else {
    rendered = JSON.stringify(content, null, 2);
  }
  const preview = rendered.length > 140 ? `${rendered.slice(0, 140)}…` : rendered;
  return (
    <details className="tool-result-card">
      <summary>← tool_result · {preview.replace(/\n/g, " ⏎ ")}</summary>
      <pre>{rendered}</pre>
    </details>
  );
}

function SystemChip({ payload }: { payload: unknown }) {
  const p = payload as SDKPayload;
  const cost = p?.total_cost_usd;
  const turns = p?.num_turns;
  const dur = p?.duration_ms;
  const stop = p?.stop_reason;
  const parts: string[] = [];
  if (p?.type) parts.push(p.type);
  if (typeof cost === "number") parts.push(`$${cost.toFixed(4)}`);
  if (typeof turns === "number") parts.push(`${turns} turn${turns === 1 ? "" : "s"}`);
  if (typeof dur === "number") parts.push(`${dur}ms`);
  if (stop) parts.push(stop);
  const label = parts.length ? parts.join(" · ") : "system event";
  return (
    <details className="system-chip">
      <summary>{label}</summary>
      <pre>{JSON.stringify(payload, null, 2)}</pre>
    </details>
  );
}

function AgentBody({ payload, filters }: { payload: unknown; filters?: Set<ContentFilter> }) {
  const blocks = extractContent(payload);
  if (!blocks) {
    return (
      <details className="payload-dump">
        <summary>raw payload</summary>
        <pre>{JSON.stringify(payload, null, 2)}</pre>
      </details>
    );
  }
  const rendered = blocks.map((block, i) => {
    if (block.type === "text" && typeof block.text === "string") {
      if (filters && !filters.has("text")) return null;
      return <TextBlock key={i} text={block.text} />;
    }
    if (block.type === "tool_use") {
      if (filters && !filters.has("tool_use")) return null;
      return <ToolUseCard key={i} block={block} />;
    }
    if (block.type === "thinking" && typeof block.text === "string") {
      if (filters && !filters.has("thinking")) return null;
      return (
        <details key={i} className="system-chip">
          <summary>thinking</summary>
          <pre>{block.text}</pre>
        </details>
      );
    }
    return (
      <details key={i} className="payload-dump">
        <summary>block: {block.type}</summary>
        <pre>{JSON.stringify(block, null, 2)}</pre>
      </details>
    );
  }).filter(Boolean);
  if (rendered.length === 0) return null;
  return <>{rendered}</>;
}

function HumanBody({ payload }: { payload: unknown }) {
  const blocks = extractContent(payload);
  const text = blocks?.find((b) => b.type === "text")?.text;
  if (typeof text === "string") {
    return <TextBlock text={text} />;
  }
  return (
    <details className="payload-dump">
      <summary>raw payload</summary>
      <pre>{JSON.stringify(payload, null, 2)}</pre>
    </details>
  );
}

function ToolBody({ payload, filters }: { payload: unknown; filters?: Set<ContentFilter> }) {
  if (filters && !filters.has("tool_result")) return null;
  const blocks = extractContent(payload);
  if (!blocks) {
    return (
      <details className="payload-dump">
        <summary>raw payload</summary>
        <pre>{JSON.stringify(payload, null, 2)}</pre>
      </details>
    );
  }
  return (
    <>
      {blocks.map((block, i) => {
        if (block.type === "tool_result") {
          return <ToolResultCard key={i} block={block} />;
        }
        return (
          <details key={i} className="payload-dump">
            <summary>block: {block.type}</summary>
            <pre>{JSON.stringify(block, null, 2)}</pre>
          </details>
        );
      })}
    </>
  );
}

export function MessageBlock({ item, contentFilters }: Props) {
  if (contentFilters && item.author === "system" && !contentFilters.has("system")) return null;
  if (contentFilters && item.author === "tool" && !contentFilters.has("tool_result")) return null;

  const body = (() => {
    if (item.author === "agent") return <AgentBody payload={item.payload} filters={contentFilters} />;
    if (item.author === "human") return <HumanBody payload={item.payload} />;
    if (item.author === "tool") return <ToolBody payload={item.payload} filters={contentFilters} />;
    if (item.author === "system") return <SystemChip payload={item.payload} />;
    return null;
  })();

  if (body === null) return null;

  return (
    <div className={`message-block author-${item.author}`}>
      <div className="message-header">
        <span className={`author-badge ${item.author}`}>{item.author}</span>
        <span className="header-timestamp">{formatLocal(item.called_at)}</span>
        <span className="header-relative">({formatRelative(item.called_at)})</span>
        <span className="header-meta">
          {item.channel ? `${item.channel} · ` : ""}{item.message_type}
        </span>
        <span className="header-session" title={item.session_id ?? ""}>
          {shortSession(item.session_id)}
        </span>
      </div>
      <div className="message-body">
        {body}
      </div>
    </div>
  );
}
